/*
 * A PYTHON RUN, AS THE PERSON READS IT — the transcript card for `sandbox.run`.
 *
 * A run drew as a tool row named after a function, its code and what it printed folded away as
 * escaped JSON. The code is the analysis and the output is the finding; both were the least
 * legible things on the screen.
 *
 * FOLDED, it is one line: what it computed (the `label` the model gave it, else the code's first
 * comment line), which datasets it loaded, how long its code ran, and — through the frame's state
 * word — whether it failed. OPEN, it is the call and what came of it:
 *
 *   · the code, coloured as it streams in (tokenised from TEXT, so half a program colours — the
 *     same reason the statechart card does it that way);
 *   · a chip per dataset: its name, its rows, where it came from, and a warning when it was cut;
 *   · what it printed, as markdown where it IS markdown (a `to_markdown()` table becomes a table)
 *     and as monospace text where it is not (`print(df)` is aligned with spaces, and markdown would
 *     collapse them); then the last expression's value and any warnings;
 *   · on a failure, what it printed first and then the traceback, styled as the error it is.
 *
 * Files it wrote are not repeated here: they are drawn under the answer, where they always were.
 *
 * NOTHING HERE CHANGES WHAT THE MODEL RECEIVES. Everything is read from the call's arguments, its
 * message and its existing `data` — see sandbox-outcome.ts for why the message is read back rather
 * than its parts copied into `data`. And everything is rendered through Lit templates or
 * `renderMarkdown`, which has no `unsafeHTML` anywhere: what the model printed cannot become markup.
 *
 * The frame — title, summary, state, the fold — is base-ui's (work-card.ts), shared with every card.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { registerWorkCard, partialString, renderMarkdown, type TurnTool, type WorkCard } from '@vitesse/base-ui/ai';
import { t, formatNumber } from '@vitesse/base-ui/i18n';
import { readOutcome, type RunReport } from './sandbox-outcome';

/** What `data` holds about each dataset on a success (sandbox-tools.ts). */
interface DatasetInfo {
  name: string;
  rows: number;
  source: string;
  truncated?: boolean;
  truncatedBy?: 'rows' | 'time' | 'historian';
}
interface RunData {
  ms?: number;
  prepareMs?: number;
  startMs?: number;
  datasets?: DatasetInfo[];
}

/* ───────────────────────────── what the arguments say ───────────────────────────── */

/** The arguments, once they parse — after the call has arrived in full. */
function parsed(raw: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(raw) as unknown;
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The first comment line of the code with something in it — not an encoding line or a shebang. */
export function firstComment(code: string): string {
  for (const line of code.split('\n')) {
    const m = /^\s*#+!?\s*(.*?)\s*$/.exec(line);
    if (!m || !m[1] || /^(?:-\*-|coding[:=]|\/)/.test(m[1])) continue;
    return m[1];
  }
  return '';
}

/** The card's title: the label, else the first comment, else "Python". Works on half an argument. */
export function runTitle(raw: string): string {
  return partialString(raw, 'label').trim() || firstComment(partialString(raw, 'code')) || t('ai.pyPython');
}

/**
 * The datasets the call names. Whole once the arguments parse; mid-stream, the names arrived so
 * far — read from the text after `"datasets"`, the way `partialString` reads one field. A `"name"`
 * inside the code cannot match: the code is a JSON string, so its quotes arrive escaped, the one
 * after `name` included.
 */
export function namedDatasets(raw: string): string[] {
  const args = parsed(raw);
  if (args) {
    const list = Array.isArray(args.datasets) ? (args.datasets as Array<{ name?: unknown }>) : [];
    return list.map((d) => (typeof d?.name === 'string' ? d.name : '')).filter(Boolean);
  }
  const at = raw.indexOf('"datasets"');
  if (at < 0) return [];
  return [...raw.slice(at).matchAll(/"name"\s*:\s*"((?:[^"\\]|\\.)*)"/g)].map((m) => m[1]!);
}

/* ───────────────────────────── the folded line ───────────────────────────── */

/** Seconds, in the reader's own number format: one decimal under ten seconds, whole above. Also how a
 *  finding's evidence says how long its run took (finding-detail.ts), so one run reads the same in both. */
export function seconds(ms: number): string {
  return t('ai.pySeconds', {
    value: formatNumber(ms / 1000, { maximumFractionDigits: ms < 10_000 ? 1 : 0, minimumFractionDigits: ms < 10_000 ? 1 : 0 }),
  });
}

export function runSummary(tool: TurnTool): string {
  const names = namedDatasets(tool.argumentsJson ?? '');
  const bits: string[] = [];
  if (names.length) bits.push(names.length <= 3 ? names.join(', ') : t('ai.pyDatasets', { count: names.length }));
  // Only a success has `data`, so only a success says how long it ran.
  const ms = (tool.data as RunData | undefined)?.ms;
  if (typeof ms === 'number') bits.push(seconds(ms));
  return bits.join(' · ');
}

/** The run's report, for a call that has settled. A success with no `data` is one the archive kept
 *  only the start of (every success carries `data`; see transcript.ts, TOOL_RESULT_INLINE_MAX). */
function reportOf(tool: TurnTool): RunReport | null {
  if (tool.state !== 'ok' && tool.state !== 'error') return null;
  const ok = tool.state === 'ok';
  return readOutcome(tool.message ?? '', ok, namedDatasets(tool.argumentsJson ?? '').length, ok && tool.data === undefined);
}

/* ───────────────────────────── the code, coloured ───────────────────────────── */

type Tok = { cls: string; text: string };

const KEYWORDS = new Set(
  'False None True and as assert async await break case class continue def del elif else except finally for from global if import in is lambda match nonlocal not or pass raise return try while with yield'.split(
    ' ',
  ),
);
const BUILTINS = new Set(
  'abs all any bool dict enumerate filter float getattr hasattr int isinstance len list map max min open print range repr round set sorted str sum super tuple type zip'.split(
    ' ',
  ),
);

/*
 * Each pattern is sticky (`y`): it matches AT the cursor or not at all. An unterminated string runs
 * to the end of its line, or of the text for a triple-quoted one — mid-stream that is the normal
 * case, and the rest of the program must not be coloured as if the string had closed.
 */
const P_COMMENT = /#[^\n]*/y;
const P_TRIPLE = /[rRbBuUfF]{0,2}("""|''')[\s\S]*?(?:\1|$(?![\s\S]))/y;
const P_STRING = /[rRbBuUfF]{0,2}(["'])(?:\\.|(?!\1)[^\\\n])*(?:\1|$)/my;
const P_DECORATOR = /@[A-Za-z_][\w.]*/y;
const P_NAME = /[A-Za-z_]\w*/y;
const P_NUMBER = /(?:0[xXoObB][\da-fA-F_]+|(?:\d[\d_]*\.?[\d_]*|\.\d[\d_]*)(?:[eE][+-]?\d+)?j?)/y;

export function tokenizePython(src: string): Tok[] {
  const out: Tok[] = [];
  const push = (cls: string, text: string): void => {
    const last = out[out.length - 1];
    if (last && last.cls === cls) last.text += text;
    else out.push({ cls, text });
  };
  const at = (re: RegExp, i: number): string | null => {
    re.lastIndex = i;
    const m = re.exec(src);
    return m && m[0] ? m[0] : null;
  };
  let i = 0;
  while (i < src.length) {
    let m: string | null;
    if ((m = at(P_COMMENT, i))) push('py-com', m);
    else if ((m = at(P_TRIPLE, i)) || (m = at(P_STRING, i))) push('py-str', m);
    else if ((m = at(P_DECORATOR, i))) push('py-dec', m);
    else if ((m = at(P_NAME, i))) {
      const after = src.slice(i + m.length).match(/^\s*\(/);
      push(KEYWORDS.has(m) ? 'py-kw' : BUILTINS.has(m) ? 'py-bi' : after ? 'py-fn' : 'py-text', m);
    } else if ((m = at(P_NUMBER, i))) push('py-num', m);
    else push('py-text', (m = src[i]!));
    i += m.length;
  }
  return out;
}

function renderPython(src: string): TemplateResult {
  return html`<pre class="wc-pre run-code">
${tokenizePython(src).map((tk) => (tk.cls === 'py-text' ? tk.text : html`<span class=${tk.cls}>${tk.text}</span>`))}</pre>`;
}

/* ───────────────────────────── what it printed ───────────────────────────── */

export type Segment = { kind: 'md' | 'pre'; text: string };

const TROW = /^\s*\|.*\|\s*$/;
const TSEP = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;
const FENCE = /^\s*(`{3,}|~{3,})/;
/** Text laid out with spaces: two or more between words, an indent, a tab. Markdown would undo it. */
const ALIGNED = /\S {2,}\S|^\s+\S|\t/;

/**
 * STDOUT, CUT WHERE MARKDOWN STARTS AND STOPS.
 *
 * The model prints two kinds of thing and both often: markdown it composed (`to_markdown()`, a
 * `## heading`, a list), and text laid out with spaces (`print(df)`, `describe()`, an f-string with
 * padding). Rendered as markdown, the second collapses — runs of spaces become one and the columns
 * fall apart. So: a markdown table, or a fenced block, is markdown whatever is around it; any other
 * paragraph is markdown unless it is laid out with spaces, and then it is shown exactly as printed.
 */
export function splitOutput(text: string): Segment[] {
  const lines = text.replace(/\s+$/, '').split('\n');
  const out: Segment[] = [];
  let gap = false;
  const add = (kind: Segment['kind'], body: string[]): void => {
    if (!body.length) return;
    const last = out[out.length - 1];
    // Markdown pieces are separate blocks to the renderer; text keeps the blank line it had, or none.
    if (last && last.kind === kind) last.text += (kind === 'md' || gap ? '\n\n' : '\n') + body.join('\n');
    else out.push({ kind, text: body.join('\n') });
    gap = false;
  };
  for (let i = 0; i < lines.length;) {
    const line = lines[i]!;
    if (!line.trim()) {
      gap = true;
      i++;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const body = [line];
      for (i++; i < lines.length; i++) {
        body.push(lines[i]!);
        if (lines[i]!.trim().startsWith(fence[1]!)) {
          i++;
          break;
        }
      }
      add('md', body);
      continue;
    }
    if (TROW.test(line) && TSEP.test(lines[i + 1] ?? '')) {
      const body: string[] = [];
      for (; i < lines.length && TROW.test(lines[i]!); i++) body.push(lines[i]!);
      add('md', body);
      continue;
    }
    // This line opens the paragraph whatever it is, so the loop always moves on.
    const para: string[] = [line];
    for (
      i++;
      i < lines.length && lines[i]!.trim() && !FENCE.test(lines[i]!) && !(TROW.test(lines[i]!) && TSEP.test(lines[i + 1] ?? ''));
      i++
    ) {
      para.push(lines[i]!);
    }
    add(para.some((l) => ALIGNED.test(l)) ? 'pre' : 'md', para);
  }
  return out;
}

function renderOutput(text: string): TemplateResult {
  return html`${splitOutput(text).map((s) => (s.kind === 'md' ? html`<div class="run-md">${renderMarkdown(s.text)}</div>` : html`<pre class="wc-pre">${s.text}</pre>`))}`;
}

/* ───────────────────────────── the card ───────────────────────────── */

/** Why a dataset was cut, in the reader's language — the chip's words, and a finding's evidence's. */
export function cutReason(by: string | undefined): string {
  return by === 'time' ? t('ai.pyCutTime') : by === 'historian' ? t('ai.pyCutHistorian') : t('ai.pyCutRows');
}

function chips(tool: TurnTool): TemplateResult | typeof nothing {
  const loaded = tool.state === 'ok' ? (tool.data as RunData | undefined)?.datasets : undefined;
  const list: DatasetInfo[] = loaded ?? namedDatasets(tool.argumentsJson ?? '').map((name) => ({ name, rows: -1, source: '' }));
  if (!list.length) return nothing;
  return html`<div class="run-chips">
    ${list.map((d) => {
      // Where it came from, short: "historian · Pump1.Flow on core", "supplied inline". The whole
      // source — window, bucket size — is the chip's tooltip.
      const from = d.source.split(' · ').slice(0, 2).join(' · ');
      const cut = d.truncated ? cutReason(d.truncatedBy) : '';
      return html`<span class="run-chip ${cut ? 'cut' : ''}" title=${d.source}>
        <b>${d.name}</b>
        ${d.rows >= 0 ? html`<span>${t('ai.pyRows', { count: d.rows, rows: formatNumber(d.rows) })}</span>` : nothing}
        ${from ? html`<span class="run-from">${from}</span>` : nothing}
        ${cut ? html`<span class="run-cut"><vitesse-icon name="triangle-alert" size="11"></vitesse-icon>${cut}</span>` : nothing}
      </span>`;
    })}
  </div>`;
}

function section(label: string, body: TemplateResult, cls = ''): TemplateResult {
  return html`<div class="run-part ${cls}">${label ? html`<div class="run-label">${label}</div>` : nothing}${body}</div>`;
}

function result(tool: TurnTool): TemplateResult | typeof nothing {
  const r = reportOf(tool);
  if (!r) return nothing;
  const parts: TemplateResult[] = [];
  if (r.failure) {
    const printed = r.failure.kind === 'stopped' ? t('ai.pyPrintedBeforeStop') : t('ai.pyPrintedFirst');
    if (r.stdout.trim()) parts.push(section(printed, renderOutput(r.stdout)));
    const text =
      r.failure.kind === 'raised'
        ? html`<pre class="wc-pre run-error">${r.failure.text.trimEnd()}</pre>`
        : html`<p class="wc-note run-error">${r.failure.text}</p>`;
    parts.push(section('', text, 'failed'));
  } else {
    if (r.stdout.trim()) parts.push(section('', renderOutput(r.stdout)));
    if (r.result) parts.push(section(t('ai.pyLastExpression'), html`<pre class="wc-pre">${r.result}</pre>`));
    if (r.stderr.trim()) parts.push(section(t('ai.pyWarnings'), html`<pre class="wc-pre run-quiet">${r.stderr}</pre>`));
    if (!r.stdout.trim() && !r.result && !r.stderr.trim() && !r.cut)
      parts.push(section('', html`<p class="wc-note run-quiet">${t('ai.pyNothing')}</p>`));
  }
  if (r.cut) parts.push(section('', html`<p class="wc-note run-quiet">${t('ai.pyCut')}</p>`));
  const d = tool.data as RunData | undefined;
  if (tool.state === 'ok' && typeof d?.ms === 'number') {
    parts.push(
      html`<div class="run-time">
        ${t('ai.pyTiming', { start: seconds(d.startMs ?? 0), prepare: seconds(d.prepareMs ?? 0), code: seconds(d.ms) })}
      </div>`,
    );
  }
  return html`${parts}`;
}

/*
 * ITS OWN STYLING, because the body is drawn inside base-ui's shadow root — the same reason the
 * statechart card carries its own. Colours are the theme's code tokens, so it follows light and dark.
 */
const styles = html`<style>
  .run-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px 0;
  }
  .run-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 2px 8px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 11px;
    line-height: 18px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .run-chip b {
    font-weight: 600;
    color: var(--text-primary);
    font-family: var(--font-mono, ui-monospace, monospace);
  }
  .run-from {
    color: var(--text-tertiary);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .run-chip.cut {
    border-color: color-mix(in srgb, var(--orange, #ff9800) 55%, var(--border-color));
  }
  .run-cut {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: var(--orange, #ff9800);
  }
  .run-code {
    color: var(--text-primary);
  }
  .py-kw {
    color: var(--cm-keyword);
  }
  .py-str {
    color: var(--cm-string);
  }
  .py-com {
    color: var(--cm-comment);
    font-style: italic;
  }
  .py-num {
    color: var(--cm-number);
  }
  .py-fn,
  .py-bi {
    color: var(--cm-function);
  }
  .py-dec {
    color: var(--cm-type);
  }
  .run-part {
    border-top: 1px solid var(--border-color);
  }
  .run-label {
    padding: 8px 12px 0;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }
  .run-md {
    padding: 2px 12px;
    font-size: 13px;
    color: var(--text-primary);
  }
  .run-error {
    color: var(--red, #e5484d);
  }
  .run-quiet {
    color: var(--text-tertiary);
  }
  .run-time {
    border-top: 1px solid var(--border-color);
    padding: 6px 12px;
    font-size: 11px;
    color: var(--text-tertiary);
  }
</style>`;

const card: WorkCard = {
  collapsible: true,
  title: (tool) => runTitle(tool.argumentsJson ?? ''),
  summary: runSummary,
  /*
   * The word beside the title. While the arguments are still arriving the frame's own "Writing…" is
   * right; once they parse the code is RUNNING, which is the wait a person is actually in. A run
   * stopped by its clock says so — "Failed" would send them looking for a bug in the code.
   */
  // The frame's own words otherwise; '' on a success, which the frame shows as no word at all.
  status: (tool) => {
    if (tool.state === 'running') return parsed(tool.argumentsJson ?? '') ? t('ai.pyRunning') : t('ai.workWriting');
    if (tool.state === 'error') return reportOf(tool)?.failure?.kind === 'stopped' ? t('ai.pyStopped') : t('ai.workFailed');
    return tool.state === 'ok' ? '' : t('ai.workReady');
  },
  body: (tool) => {
    const code = partialString(tool.argumentsJson ?? '', 'code');
    return html`${styles}${chips(tool)}${code ? renderPython(code) : nothing}${result(tool)}`;
  },
};

/** Claim `sandbox.run`. Returns the undo. */
export function registerSandboxCard(): () => void {
  return registerWorkCard('sandbox.run', card);
}
